import { useEffect, useState } from 'react';
import { getRestaurantsByBrandID } from '../api/brand';
import { SelectableLocation } from '../components/SelectLocationsModal';
import { useBrandContext } from '../contexts/BrandContext';
import { useRestaurantContext } from '../contexts/RestaurantContext';

const formatLocationLabel = (restaurant: any): string => {
  const street = [restaurant.address, restaurant.address2].filter(Boolean).join(' ');
  const stateZip = [restaurant.state, restaurant.zip].filter(Boolean).join(' ');
  const address = [street, restaurant.city, stateZip].filter(Boolean).join(', ');
  return address || restaurant.name || `Location ${restaurant.restaurant_id}`;
};

/**
 * Every location of the brand the current location belongs to, so SAVE options can target them.
 * Empty until loaded, or when the brand can't be found.
 */
const useBrandLocations = (): SelectableLocation[] => {
  const [brandLocations, setBrandLocations] = useState<SelectableLocation[]>([]);
  const { currentRestaurant } = useRestaurantContext();
  const { brands, currentBrand } = useBrandContext();

  useEffect(() => {
    const restaurantID = currentRestaurant?.restaurantID;
    if (!restaurantID || !brands?.length) return undefined;

    let isCancelled = false;
    const loadBrandLocations = async () => {
      // check the selected brand first; it's usually the one the location belongs to
      const orderedBrands = [...brands].sort(
        (a, b) => Number(b.id === currentBrand?.id) - Number(a.id === currentBrand?.id)
      );
      // eslint-disable-next-line no-restricted-syntax
      for (const brand of orderedBrands) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const restaurants = await getRestaurantsByBrandID(brand.id);
          if (restaurants?.some((restaurant) => restaurant.restaurant_id === restaurantID)) {
            if (!isCancelled) {
              setBrandLocations(
                restaurants.map((restaurant) => ({
                  id: restaurant.restaurant_id,
                  label: formatLocationLabel(restaurant)
                }))
              );
            }
            return;
          }
        } catch (error) {
          console.error('Error fetching brand locations:', error);
        }
      }
      if (!isCancelled) setBrandLocations([]);
    };

    loadBrandLocations();
    return () => {
      isCancelled = true;
    };
  }, [brands, currentBrand?.id, currentRestaurant?.restaurantID]);

  return brandLocations;
};

export default useBrandLocations;
